/**
 * @file Footer.jsx
 * @description Shared footer for the redesigned pages.
 *              Clean, light-themed footer with grouped links and social icons.
 * @module components/layout/Footer
 */
import React from "react";
import { useAuth } from "../../context/AuthContext";
import { FiInstagram, FiYoutube, FiArrowUpRight } from "react-icons/fi";

/* ══════════════════════════════════════════════════════════════
 * DATA & CONFIG
 * ══════════════════════════════════════════════════════════════ */
const PLATFORM_LINKS = [
  { label: "Documentation", path: "/documentation" },
  { label: "Support", path: "/support" },
  { label: "Careers", path: "/careers" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Service", path: "/terms-of-service" },
  { label: "Contact", path: "/contact" },
];

const SOCIAL_LINKS = [
  {
    label: "Instagram",
    href: "https://instagram.com",
    icon: FiInstagram,
    hoverClass: "hover:bg-gradient-to-r hover:from-pink-500 hover:to-purple-600 hover:text-white",
  },
  {
    label: "YouTube",
    href: "https://youtube.com",
    icon: FiYoutube,
    hoverClass: "hover:bg-red-600 hover:text-white",
  },
];

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component Footer
 * @description The main footer component used across the application.
 *
 * @param {Object} props
 * @param {Function} props.navigate - App-level navigation function.
 * @param {Array} props.navLinks - Optional array of navigation links to display as quick links.
 * @returns {JSX.Element} The rendered Footer view.
 */
const Footer = ({ navigate, navLinks = [] }) => {
  const { isAuthenticated } = useAuth();

  const quickLinks = navLinks.length > 0
    ? navLinks
    : [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") },
      ];

  return (
    <footer className="bg-emerald-900">
      <div className="max-w-8xl mx-auto px-8 sm:px-12 lg:px-16 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* ── Brand ── */}
          <div className="hidden lg:flex items-start">
            <button
              onClick={() => navigate("/")}
              className="focus:outline-none"
              aria-label="Go to homepage"
            >
              <img
                src="/logo.png"
                alt="SimpleCode"
                className="h-10 w-auto hover:opacity-80 transition-opacity duration-200"
              />
            </button>
          </div>

          {/* ── Quick Links ── */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-white text-left">
              Quick Links
            </h4>
            <div className="space-y-3">
              {quickLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={link.action}
                  className="group flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors duration-200 w-max"
                >
                  <span className="underline decoration-dotted underline-offset-4 decoration-gray-500 group-hover:decoration-gray-300">{link.label}</span>
                  <FiArrowUpRight className="w-3.5 h-3.5 text-gray-500 group-hover:text-white transition-colors duration-200" />
                </button>
              ))}
            </div>
          </div>

          {/* ── Platform ── */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-white text-left">Platform</h4>
            <div className="space-y-3">
              {PLATFORM_LINKS.map((link) => (
                <button
                  key={link.label}
                  onClick={() => navigate(link.path)}
                  className="group flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors duration-200 w-max"
                >
                  <span className="underline decoration-dotted underline-offset-4 decoration-gray-500 group-hover:decoration-gray-300">{link.label}</span>
                  <FiArrowUpRight className="w-3.5 h-3.5 text-gray-500 group-hover:text-white transition-colors duration-200" />
                </button>
              ))}
            </div>
          </div>

          {/* ── Follow Us ── */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-white text-left">
              Follow Us
            </h4>
            <div className="flex gap-3">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow us on ${social.label}`}
                  className={`w-9 h-9 bg-gray-300 rounded-full flex items-center justify-center text-muted-foreground transition-all duration-300 ${social.hoverClass}`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <p className="text-xs text-gray-400 text-left">
              Join our community for coding tips, tutorials, and updates!
            </p>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="mt-10 pt-6 border-t-2 border-gray-400 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} SimpleCode. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            {LEGAL_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={() => navigate(link.path)}
                className="text-gray-400 hover:text-white transition-colors duration-200 hover:underline hover:decoration-dotted hover:underline-offset-4 hover:decoration-white"
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
